import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useColumnVisibility } from '../../contexts/ColumnVisibilityContext';

const Sidebar = ({ 
  isCollapsed = false, 
  onToggleCollapse, 
  userRole = 'dispatcher', 
  notificationCounts = {}
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isColumnsHidden, toggleColumnsVisibility } = useColumnVisibility();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/administrative-dashboard',
      icon: 'LayoutDashboard',
      roles: ['admin', 'dispatcher'],
      badge: 0,
      description: 'Verwaltungs-Dashboard'
    },
    {
      label: 'Mitarbeiter',
      path: '/employee-management',
      icon: 'UserCheck',
      roles: ['admin', 'dispatcher'],
      badge: 0,
      description: 'Mitarbeiter-Verwaltung'
    },
    // {
    //   label: 'Schichtplanung',
    //   path: '/shift-planning',
    //   icon: 'Calendar',
    //   roles: ['admin', 'dispatcher'],
    //   badge: 0,
    //   description: 'Schicht-Planung'
    // },
    {
      label: 'Zeitkorrekturen',
      path: '/time-entry-corrections',
      icon: 'Edit3',
      roles: ['admin', 'dispatcher'],
      badge: 0,
      description: 'Zeit-Einträge korrigieren'
    },
    {
      label: 'Zeiterfassung',
      path: '/kiosk-clock-interface',
      icon: 'Clock',
      roles: ['admin', 'dispatcher', 'technician', 'field_worker'],
      badge: 0,
      description: 'Kiosk Zeiterfassung'
    }
    // {
    //   label: 'Audit-Trail',
    //   path: '/audit-trail-review',
    //   icon: 'FileText',
    //   roles: ['admin', 'dispatcher'],
    //   badge: 0,
    //   description: 'Audit-Protokoll'
    // }
  ];

  const filteredNavItems = navigationItems?.filter(item => 
    item?.roles?.includes(userRole)
  );

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActivePath = (path) => {
    if (path === '/kiosk-clock-interface' && location?.pathname === '/clock-success-confirmation') {
      return true;
    }
    return location?.pathname === path;
  };

  const Logo = () => (
    <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-4`}>
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="Car" size={20} color="white" />
        </div>
        {!isCollapsed && (
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-primary">Ragna Detailing</span>
            <span className="text-xs text-text-secondary -mt-1">Mitarbeiter Verwaltung</span>
          </div>
        )}
      </div>
      {onToggleCollapse && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          className={`${isCollapsed ? 'h-8 w-8 p-0' : 'h-8 w-8 p-0'}`}
          title={isCollapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'}
        >
          <Icon name={isCollapsed ? "ChevronRight" : "X"} size={16} />
        </Button>
      )}
    </div>
  );

  return (
    <aside className={`fixed left-0 top-0 h-full bg-card border-r border-border z-1000 transition-all duration-200 ease-out ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="border-b border-border">
          <Logo />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          {filteredNavItems?.map((item) => (
            <div key={item?.path} className="relative">
              <button
                onClick={() => handleNavigation(item?.path)}
                className={`w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors duration-150 group ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
                title={isCollapsed ? item?.label : ''}
              >
                <Icon 
                  name={item?.icon} 
                  size={18} 
                  className={`flex-shrink-0 ${!isCollapsed ? 'mr-3' : ''}`} 
                />
                {!isCollapsed && (
                  <>
                    <div className="flex-1 text-left">
                      <div className="font-medium">{item?.label}</div>
                      <div className="text-xs opacity-75">{item?.description}</div>
                    </div>
                    {item?.badge > 0 && (
                      <span className="bg-accent text-accent-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium ml-2">
                        {item?.badge > 99 ? '99+' : item?.badge}
                      </span>
                    )}
                  </>
                )}
              </button>

              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-modal opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-1100">
                  {item?.label}
                  {item?.badge > 0 && (
                    <span className="ml-2 bg-accent text-accent-foreground text-xs rounded-full w-5 h-5 inline-flex items-center justify-center font-medium">
                      {item?.badge > 99 ? '99+' : item?.badge}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Logout Button - Middle Section */}
        <div className="px-2 py-80">
          <button
            variant="ghost"
            size="sm"
            onClick={() => {
              toggleColumnsVisibility();
              // Toggle the Aktionen column visibility
              const aktionenColumns = document.querySelectorAll('[data-column="Aktionen"]');
              aktionenColumns.forEach(column => {
                if (isColumnsHidden) {
                  column.style.display = '';
                } else {
                  column.style.display = 'none';
                }
              });
            }}
            className="w-3 h-24 justify-center text-red-600"
            title=''
          >
            
            {!isCollapsed && <span className="ml-2"></span>}
          </button>
        </div>

        {/* Collapse Toggle */}
        {onToggleCollapse && (
          <div className="border-t border-border p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              className="w-full justify-center"
              title={isCollapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'}
            >
              <Icon 
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"} 
                size={16} 
              />
              {!isCollapsed && <span className="ml-2">Einklappen</span>}
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;